import clsx from 'clsx';
import { differenceInCalendarDays } from 'date-fns';
import { isArray, isEmpty, toNumber } from 'lodash';
import { array, string } from 'prop-types';
import { Fragment, useEffect, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, createSearchParams, useNavigate, useParams } from 'react-router-dom';

import { Badge, Banner, Card, ReactTable, Skeleton, ZendeskLink } from 'components';
import { formatDate, formatLocalizedCurrency, getBadgeColor, getValidDate } from 'helpers';
import { FEATURE_FLAG_KEYS, useFeatureFlag, useFetch, useUser } from 'hooks';
import { routes } from 'router';

const ParticipantsList = ({ participants, className = '' }) => {
  const { t } = useTranslation();

  const participantsColumns = useMemo(
    () => [
      {
        accessorKey: 'name',
        enableSorting: false,
        header: t('dashboard:show.participantsTable.lastName')
      },
      {
        accessorKey: 'firstName',
        enableSorting: false,
        header: t('dashboard:show.participantsTable.firstName')
      },
      {
        accessorKey: 'birthDate',
        enableSorting: false,
        header: t('dashboard:show.participantsTable.birthDate')
      }
    ],
    [participants]
  );

  return (
    <Card onMobile={false} className={clsx('col-span-12 lg:col-span-7', className)}>
      <h2 className="h2 underlined-yellow mb-8">{t('dashboard:show.participants')}</h2>
      <ReactTable
        columns={participantsColumns}
        data={participants}
        pagination={participants?.length > 10}
        className="participant-table"
      />
    </Card>
  );
};
const TravelOrderShow = () => {
  const faqLink = process.env.REACT_APP_ZENDESK_URL_ORDER_DETAIL;

  const { t } = useTranslation();
  const { currentUser } = useUser();
  const { id } = useParams();
  const navigate = useNavigate();

  const { enabled: newTravelTunnelActive } = useFeatureFlag(FEATURE_FLAG_KEYS.newTravelTunnelTouloisirs);

  const { call: orderCall, loading: orderLoading, data: orderData } = useFetch();

  useEffect(() => {
    getTravelOrder();
  }, [currentUser]);

  const getTravelOrder = () => {
    orderCall({
      service: 'castelis',
      path: `order/${currentUser.serialNumber}/travelOrderDetail/${id}`,
      method: 'get'
    });
  };

  const transactionsColumns = useMemo(
    () => [
      {
        accessorKey: 'title',
        enableSorting: false,
        header: t('dashboard:show.type')
      },
      {
        accessorKey: 'date',
        enableSorting: false,
        header: t('dashboard:table.header.date'),
        cell: (cell) => <span>{formatDate(getValidDate(cell.getValue()))}</span>
      },
      {
        accessorKey: 'amount',
        enableSorting: false,
        header: t('dashboard:table.header.amount'),
        cell: (cell) => {
          const { credit, debit } = cell?.row?.original || {};
          const isCredit = toNumber(credit) > 0;
          const amount = formatLocalizedCurrency(isCredit ? credit : debit);
          return <>{amount && <span>{amount}</span>}</>;
        }
      }
    ],
    [orderData?.transactions]
  );

  const showPayBalance = useMemo(() => {
    if (isEmpty(orderData?.transactions)) return false;

    const { balance } = orderData.transactions.slice(-1)?.[0] || {};
    /*
      balance is a string and should be converted to number for comparison
      Balance can sometimes be a negative scientific notation e.g 1.2345e-10
      toNumber will convert it to 0.00000000012345
      toFixed(0) will remove the decimals
      => result: '0' (string)
    */
    const latestTransaction = toNumber(balance).toFixed(0);
    return toNumber(latestTransaction) > 0;
  }, [orderData?.transactions]);

  const summary = useMemo(() => {
    return [
      { i18nKey: 'reference', value: orderData?.ref },
      { i18nKey: 'stay', value: orderData?.stay?.title },
      { i18nKey: 'status', value: orderData?.status },
      { i18nKey: 'travelDates', value: [orderData?.stay?.fromDate, orderData?.stay?.toDate] },
      { i18nKey: 'destination', value: orderData?.stay?.country },
      {
        i18nKey: 'accomodation',
        value: orderData?.lodgings?.map((lodging) => lodging.title)?.join(' + ') || ''
      },
      { i18nKey: 'createDate', value: orderData?.date },
      { i18nKey: 'priceBefore', value: formatLocalizedCurrency(orderData?.order?.amount) },
      { i18nKey: 'priceAfter', value: formatLocalizedCurrency(orderData?.order?.amountAfterSubsidy) }
    ];
  }, [orderData]);

  const daysBeforeStart = useMemo(() => {
    if (orderData?.stay?.fromDate) {
      const startDate = getValidDate(orderData?.stay?.fromDate);
      const today = new Date();
      return differenceInCalendarDays(startDate, today);
    }
    return 0;
  }, [orderData?.stay?.fromDate]);

  const goBack = () => {
    navigate(routes.dashboard.children.orders.path);
  };

  const handleBalanceRedirection = () => {
    if (newTravelTunnelActive) {
      navigate({
        pathname: routes.travelBalance.children.validation.path,
        search: createSearchParams({ orderId: `${orderData?.id}` }).toString()
      });
    } else {
      // eslint-disable-next-line
      window.location.href = `${process.env.REACT_APP_CASTELIS_TUNNEL_URL}/VoyageCommand/settlePayment?id=${orderData?.id}&nobank=0&token=${currentUser.token}`;
    }
  };

  return (
    <>
      {orderLoading && (
        <div className="grid grid-cols-12 gap-y-8 lg:gap-8">
          <div className="col-span-12 flex flex-col gap-y-8 lg:gap-8 lg:col-span-7">
            <Skeleton type="text" classNames="h-12 w-72 lg:hidden" />
            <Skeleton type="text" classNames="h-80 w-full lg:h-[35rem]" />
            <Skeleton type="text" classNames="h-12 w-72 lg:hidden" />
            <Skeleton type="text" classNames="h-48 w-full" />
          </div>
          <div className="col-span-12 gap-8 flex flex-col lg:col-span-5">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={`card-skeleton-${index}`} className="rounded-xl overflow-hidden">
                <Skeleton type="text" classNames="h-12 w-72 lg:hidden" />
                <Skeleton type="text" classNames="h-48 w-full lg:h-48" />
              </div>
            ))}
          </div>
        </div>
      )}
      {orderData && !orderLoading && (
        <>
          {daysBeforeStart > 0 && (
            <Banner type="warning" icon={{ name: 'credit-card', style: 'fad' }}>
              <span>
                <Trans i18nKey="dashboard:show.bannerBalance" values={{ count: daysBeforeStart }} />
              </span>
            </Banner>
          )}

          <button type="button" className="btn uppercase my-6 small" onClick={goBack}>
            {t('buttons.goBack')}
          </button>

          <div className="grid grid-cols-12 gap-y-8 lg:gap-8">
            <div className="col-span-12 lg:col-span-7 flex flex-col gap-8">
              <Card onMobile={false} className="relative pb-16">
                <div className="flex-center-between mb-8">
                  <h2 className="h2 underlined-yellow">{t('dashboard:show.summary')}</h2>

                  <div className="flex justify-end gap-4 absolute bottom-0 right-0 md:static">
                    {!isEmpty(orderData.docs) && (
                      <a href={`${orderData.docs}&token=${currentUser.token}`} className="btn mt-4 small outlined">
                        {t('dashboard:show.invoice')}
                      </a>
                    )}

                    <ZendeskLink to={faqLink} className="btn uppercase mt-4 small">
                      {t('dashboard:show.manage')}
                    </ZendeskLink>
                  </div>
                </div>

                <div className="grid grid-cols-12 gap-6 text-gray-600">
                  {summary.map((item, i) => (
                    <Fragment key={`summary-${i}`}>
                      <div className="col-span-5 uppercase font-semibold">{t(`dashboard:show.${item.i18nKey}`)}</div>

                      <div className="col-span-7">
                        {isArray(item.value) && (
                          <div className="flex flex-wrap gap-3">
                            {item.value.map((value, j) => (
                              <div key={`summary-value-${j}`}>{value}</div>
                            ))}
                          </div>
                        )}

                        {item.i18nKey === 'status' && (
                          <Badge color={getBadgeColor(item.value)} className="text-sm">
                            {item.value}
                          </Badge>
                        )}

                        {!isArray(item.value) && item.i18nKey !== 'status' && item.value}
                      </div>
                    </Fragment>
                  ))}
                </div>
              </Card>

              <ParticipantsList participants={orderData.participants} className="hidden lg:block" />
            </div>

            <div className="col-span-12 flex flex-col gap-y-8 lg:gap-8 lg:col-span-5">
              <Card onMobile={false}>
                <h2 className="h2 underlined-yellow mb-8">{t('dashboard:show.travelDoc')}</h2>
                <Banner type="warning" icon={{ name: 'clock', style: 'fad' }}>
                  <p>{t('dashboard:show.bannerDoc')}</p>
                </Banner>
              </Card>

              <Card onMobile={false} className="relative pb-16">
                <div className="flex-center-between mb-8">
                  <h2 className="h2 underlined-yellow">{t('dashboard:show.transactions')}</h2>

                  {showPayBalance && (
                    <button
                      type="button"
                      className="btn small uppercase absolute bottom-0 right-0 md:static"
                      onClick={handleBalanceRedirection}
                    >
                      {t('dashboard:show.payBalance')}
                    </button>
                  )}
                </div>
                <ReactTable columns={transactionsColumns} data={orderData.transactions} />
              </Card>

              {orderData?.insurances?.length > 0 && (
                <Card onMobile={false} className="relative pb-16">
                  <div className="flex-center-between mb-8">
                    <h2 className="h2 underlined-yellow relative">{t('dashboard:show.insurance')}</h2>
                    <Link to={routes.insurance.path} className="btn small absolute bottom-0 right-0 md:static">
                      {t('dashboard:show.moreDetails')}
                    </Link>
                  </div>

                  <div className="grid grid-cols-12 gap-4 text-gray-600">
                    <p className="col-span-5 uppercase font-semibold">{t('dashboard:show.label')}</p>
                    <p className="col-span-7">{orderData.insurances[0].title}</p>

                    <p className="col-span-5 uppercase font-semibold">{t('dashboard:show.type')}</p>
                    <p className="col-span-7">{orderData.insurances[0].type}</p>

                    <p className="col-span-5 uppercase font-semibold">{t('dashboard:show.price')}</p>
                    <p className="col-span-7">
                      {formatLocalizedCurrency(orderData.insurances[0].amount)}
                      {t('unitPrice')}
                    </p>
                  </div>
                </Card>
              )}

              <Card onMobile={false}>
                <h2 className="h2 underlined-yellow mb-8">{t('dashboard:show.delivery')}</h2>

                <div className="grid grid-cols-12 gap-4 text-gray-600">
                  <p className="col-span-5 uppercase font-semibold">{t('dashboard:show.type')}</p>
                  <p className="col-span-7">{orderData.delivery.type}</p>

                  <p className="col-span-5 uppercase font-semibold">{t('dashboard:show.address')}</p>
                  <div className="col-span-7 flex flex-col">
                    <p>
                      {orderData.delivery.firstName} {orderData.delivery.name}
                    </p>
                    <p>{orderData.delivery.address1}</p>
                    {!!orderData.delivery?.address2 && <p>{orderData.delivery.address2}</p>}
                    {!!orderData.delivery?.address3 && <p>{orderData.delivery.address3}</p>}
                    <p>
                      {orderData.delivery.postalCode} {orderData.delivery.city}
                    </p>
                  </div>
                </div>
              </Card>
            </div>

            <ParticipantsList participants={orderData.participants} className="lg:hidden" />
          </div>
        </>
      )}
    </>
  );
};

ParticipantsList.propTypes = {
  participants: array,
  className: string
};

export default TravelOrderShow;
